<template>
    <div class="container-fluid">
        <PageHeader :page="page" >
            <div class="d-flex justify-content-end h-100 align-items-end">
                <ActionButton
                    text="Regresar"
                    class="m-2"
                    i="fas fa-arrow-circle-left"
                    variant="outline-success"
                    @click="$router.go(-1)"
                />
                <ActionButton
                    text="Someter"
                    class="m-2"
                    i="fas fa-flag"
                    variant="outline-success"
                     @click="handleSubmit"
                />
                <ActionButton
                    text="Guardar"
                    class="m-2"
                    i="fas fa-save"
                    variant="success"
                    @click="handleSave"
                />
            </div>
        </PageHeader>

        <ValidationObserver ref="request-form">
          <RequestTypeSelector
              v-model="requestTypeSelected"
              :options="requestTypeOptions"
          />

          <RequestTypeDisclaimer
              :options="requestTypeOptions"
          />

          <div v-if="requestTypeSelected?.id">
                <div class="section-title__container">
                    <span class="dot-icon text-center mr-2">
                        <i id="icon-info-circle" class="fas fa-info-circle" />
                    </span>
                    <b>Listado de estudiantes pendientes a solicitar recursos</b>
                </div>

                <div class="search-filter__container">
                    <CustomInput
                        left-icon
                        v-model="searchText"
                        style="min-width: 400px;"
                        icon="fas fa-search orange-icon"
                        placeholder="Escriba Número de SIE o Nombre del Estudiante"
                    />
                    <ActionButton
                        text="Buscar"
                        class="ml-3"
                        variant="success"
                        i="fas fa-search"
                        @click="applyFilter"
                    />
                </div>

                <div class="result__container">
                    <VacantPositionResult
                        v-for="vacantPosition in filteredResult"
                        :key="`${vacantPosition.saeE10Id}-${requestTypeSelected.id}`"
                        :value="vacantPosition"
                        :disabled="isSelectionDisabled && !vacantPosition.selected && !selectedUniqueSies.some(sieId => vacantPosition.studentSieId === sieId)"
                        :collections="collections"
                        :require-documents="!isGroupRequest"
                        :custodian-students="custodianStudent"
                        @toggle-select="handleToggleSelection"
                        @custodian-redirect="custodianRedirect"
                    />
                    <custom-result-not-found
                        v-if="!filteredResult.length"
                        text="No cuenta con más SAEE10 para solicitar recurso."
                    />
                </div>

                <div v-if="isGroupRequest">
                    <div class="section-title__container">
                        <span class="dot-icon text-center mr-2">
                            <i id="icon-user-tie" class="fas fa-user-tie" />
                        </span>
                        <b>Listado de Maestros</b>
                    </div>
                    <div class="search-filter__container">
                        <validation-provider
                            v-slot="{ errors }"
                            rules="required"
                        >
                            <custom-drop-down
                                id="custodian"
                                style="min-width: 400px"
                                v-model="custodian"
                                label="Seleccione Maestro a asignar:"
                                fieldkey="id"
                                fieldname="name"
                                placeholder="Seleccione"
                                :error="errors[0]"
                                custom-error-msg="Favor seleccionar un Maestro"
                                :options="collections.custodians"
                                :clearable="false"
                            />
                        </validation-provider>
                    </div>
                </div>
          </div>
        </ValidationObserver>
    </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import { ValidationProvider } from 'vee-validate';
import formDataUtil from "@/utils/formDataUtil";
import documentsService from "@/services/documents";
import { getNecessitiesList } from "@/modules/human-resources/api";
import CustomDropDown from "custom-drop-down";
import CustomResultNotFound from 'custom-result-not-found';
import RequestTypeSelector from './RequestTypeSelector.vue';
import RequestTypeDisclaimer from './RequestTypeDisclaimer.vue';
import VacantPositionResult from './VacantPositionResult.vue';
import {
    GetCustodians,
    GetVacantPositions,
    CreateNewRequest,
    CreateRequestDocuments,
    SubmitNewRequest
} from '@/modules/peiSAEE/api.js';
import { getStudentParams } from "@/modules/human-resources/api";
import {requestTypeCodes} from "@/utils/constants/hrrequest/requestTypeCodes";
import mixAlert from "@/mixin/alert.mixin";

import requestTypeRadioOptions from "./RequestTypeRadioOptions";
const SUCCESS_MESSAGE = "La información ha sido guardada de manera exitosa.";
const STORABLE_PAGE = "locationAlternativeManagement";
const KEY_STORAGE = "irregular-request-storage";

export default {
    mixins: [mixAlert],
    components: {
        PageHeader,
        ActionButton,
        CustomInput,
        CustomDropDown,
        CustomResultNotFound,
        RequestTypeSelector,
        RequestTypeDisclaimer,
        VacantPositionResult,
        ValidationProvider,
    },
    data: () => ({
        page: {
            icon: 'fas fa-users-class py-1',
            title: 'Solicitud de Persona Irregular',
            routes: ['Inicio', 'Recursos Humanos', 'Persona Irregular'],
        },
        requestTypesLimits: {
            studentMax: 0,
            studentMin: 0,
            groupMax: 0,
            groupMin: 0,
            twoOrMoreMax: 0,
            twoOrMoreMin: 0,
        },
        requestTypeSelected: null,
        requestTypeOptions: requestTypeRadioOptions,
        result: [],
        filteredResult: [],
        searchText: null,
        custodian: null,
        collections: {
          documentTypes: [],
          necessities: [],
          custodians: []
        },
        oldState: null,
    }),
    computed: {
        schoolId() {
            return this.$route.query?.schoolId;
        },
        academicYearId() {
            return this.$route.query?.academicYearId;
        },
        custodianStudent() {
            return this.requestTypeSelected?.id == requestTypeCodes.Grupo ? this.custodian?.students : null;
        },
        selectedUniqueSies() { 
            const selectedList = this.result.filter(x => x.selected).map(x => x.studentSieId);
            const uniqueSies = Array.from(new Set(selectedList)); // no duplicated sies
            return uniqueSies;
        },

        isSelectionDisabled() {
            if (!this.requestTypeSelected) return true;

            const selectedSiesCount = this.selectedUniqueSies.length;

            switch (this.requestTypeSelected.id) {
                case requestTypeCodes.Estudiantes: return selectedSiesCount >= this.requestTypesLimits.studentMax;
                case requestTypeCodes.DosMasEstudiantes: return selectedSiesCount >= this.requestTypesLimits.twoOrMoreMax;
                case requestTypeCodes.Grupo: return selectedSiesCount >= this.requestTypesLimits.groupMax;
            }
        },
        isGroupRequest() {
            return this.requestTypeSelected?.id == requestTypeCodes.Grupo;
        },
        studentSiesSelected() {
          const sies = this.result.filter(x => x.selected).map(x => x.studentSieId);
          return [...new Set(sies)];
        },
        saee10IdsSelected() {
            return this.result
                .filter(x => x.selected)
                .map(x => x.saeE10Id);
        },
        saee10Documents() {
            return this.result.filter(x => x.selected).flatMap(x => {
              return x.documents?.map(document => ({
                studentSieId: x.studentSieId,
                necessityId: document.necessityId,
                deleted: !x.selected,
                document
              }));
            });
        },
    },
    methods: {
        async fetchResources() {
          const resultDocumentTypes = await documentsService.getDocumentTypes(
            "HRRequest"
          );
          this.collections.documentTypes = resultDocumentTypes.data;

          const resultNecesities = await getNecessitiesList();
          this.collections.necessities = resultNecesities.data;

          const { data } = await getStudentParams();
            this.requestTypesLimits = {
                studentMax: 1,
                studentMin: 1,
                groupMax: data.requestPersonalGroupMax,
                groupMin: data.requestPersonalGroupMin,
                twoOrMoreMax: data.requestPersonalTwoOrMoreMax,
                twoOrMoreMin: data.requestPersonalTwoOrMoreMin,
            };
        },
        async fetchVacantPositions() {
            const { data } = await GetVacantPositions(this.schoolId, this.academicYearId);
            this.result = data;
            this.searchText = null,
            this.filteredResult = data;
            this.custodian = null;
        },
        async fetchCustodians() {
            const { data } = await GetCustodians(this.schoolId);
            this.collections.custodians = data;
        },
        async handleSubmit() {
          try {
            if (!this.isSafeSubmit()) return;

            const isValidRequest = await this.$refs["request-form"].validate();
            if (!isValidRequest) {
              this.ShowToast(
                  'Error',
                  'Favor de completar la información solicitada.',
                  'error'
              );
              return;
            }

            const formData = new FormData();
            formDataUtil.build(formData, this.saee10IdsSelected, "request.saee10Ids");
            formDataUtil.build(formData, this.requestTypeSelected?.id, "request.type");
            formDataUtil.build(formData, this.custodian?.id, "request.custodianId");
            formDataUtil.build(formData, this.saee10Documents, "documents.requestDocuments");

            await SubmitNewRequest(formData);

            this.ShowToast('', SUCCESS_MESSAGE, 'success');
            await this.fetchResources();
            await this.fetchVacantPositions();
            await this.fetchCustodians();
            this.restoreState();
          } catch (error) {
            const message =
                error?.response?.data?.message ??
                "Error al someter la solicitud";
            this.ShowToast("", message, "error");
          }
        },
        async handleSave() {
            try {
                if (!this.saee10IdsSelected.length) return;

                const request = {
                    saee10Ids: this.saee10IdsSelected,
                    type: this.requestTypeSelected?.id,
                    custodianId: this.custodian?.id
                }

                const { data } = await CreateNewRequest(request);

                if (this.saee10Documents.length) {
                    const formData = new FormData();
                    formDataUtil.build(formData, data, "requestId");
                    formDataUtil.build(formData, this.saee10Documents, "requestDocuments");
                    await CreateRequestDocuments(formData);
                }

                this.ShowToast('', SUCCESS_MESSAGE, 'success');
                this.$router.push(`/humanres/irregularstaffrequest/${data}`);
            }
            catch (error) {
              const message =
                  error?.response?.data?.message ??
                  "Error al crear la solicitud";

                this.ShowToast("", message, "error");
            }
        },
        isSafeSubmit() {
          let requireSelection = 0;
          if (
            this.requestTypeSelected.id == requestTypeCodes.Estudiantes &&
            this.studentSiesSelected.length < this.requestTypesLimits.studentMin
          ) {
            requireSelection = this.requestTypesLimits.studentMin;
          } else if (
            this.requestTypeSelected.id == requestTypeCodes.DosMasEstudiantes &&
            this.studentSiesSelected.length < this.requestTypesLimits.twoOrMoreMin
          ) {
            requireSelection = this.requestTypesLimits.twoOrMoreMin;
          } else if (
            this.requestTypeSelected.id == requestTypeCodes.Grupo &&
            this.studentSiesSelected.length < this.requestTypesLimits.groupMin
          ) {
            requireSelection = this.requestTypesLimits.groupMin
          }
          if (requireSelection) {
            this.ShowToast(
              "Alerta",
              `Favor seleccionar al menos ${requireSelection} estudiante${requireSelection > 1 ? 's' : ''}.`,
              "error"
            );
            return false;
          }
          return true
        },
        applyFilter() {
            if (!this.searchText) {
                this.filteredResult = this.result;
                return;
            }

            this.filteredResult = this.result.filter(
                item => `${item.studentSieId} - ${item.studentName}`.includes(this.searchText)
            );
        },
        handleToggleSelection(saeE10Id, value) {
            const vacantPositionResult = this.result.find(x => x.saeE10Id == saeE10Id);
            vacantPositionResult.selected = value;
        },
        restoreState() {
            if (!this.oldState) return;

            this.requestTypeSelected = requestTypeRadioOptions
                .find(x => x.id == this.oldState.requestTypeId);
            
            this.searchText = this.oldState.searchText;
            
            this.oldState.saee10Ids.forEach(id => {
                const saee10 = this.result.find(x => id == x.saeE10Id);
                saee10.selected = true;
            });

            this.custodian = this.collections.custodians
                .find(x => x.id == this.oldState.custodianId);

            this.oldState = null;
        },
        custodianRedirect() {
            this.$router.push({ path: '/humanres/locationalternativemanagement' });
        },
    },
    beforeRouteLeave (to, from , next) {
        if (STORABLE_PAGE != to.name) next();
        
        const state = {
            requestTypeId: this.requestTypeSelected?.id,
            custodianId: this.custodian?.id,
            saee10Ids: this.saee10IdsSelected,
            searchText: this.searchText
        };

        localStorage.setItem(
            KEY_STORAGE,
            JSON.stringify(state));

        next();
    },
    beforeRouteEnter(_, from, next) {
        next((vm) => {
            if (STORABLE_PAGE != from.name) {
                localStorage.removeItem(KEY_STORAGE);
            }

            vm.oldState = JSON.parse(localStorage.getItem(KEY_STORAGE));
        });
    },
    watch: {
        requestTypeSelected: {
            deep: true,
            handler(newValue, oldValue) {
                if(!oldValue) return;

                this.searchText = null;
                this.custodian = null;
                this.result.forEach(x => x.selected = false);
            },
        },
    },
    async mounted() {
        await this.fetchResources();
        await this.fetchVacantPositions();
        await this.fetchCustodians();
        this.restoreState();
    },
};
</script>

<style scoped>
@import './common.scss';
</style>
